// src/components/DevStream/utils/eventParsers.js

/**
 * Helper utilities for parsing DevStream event data
 *
 * Handles multiple event formats:
 * - SNS messages (Yonomi Platform)
 * - Mobile Credentials webhooks
 * - System errors
 */

/**
 * Safely parse Message field if it's a JSON string
 */
export const getParsedMessage = (data) => {
  if (data?.Message && typeof data.Message === "string") {
    try {
      return JSON.parse(data.Message);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Extract event source from multiple possible locations
 */
export const extractSource = (data) => {
  return data?._devStreamMetadata?.source || data?.source || "UNKNOWN";
};

/**
 * Extract event type - checks multiple locations based on source
 */
export const extractEventType = (data, parsedMessage) => {
  return (
    data?.MessageAttributes?.type?.Value || // SNS MessageAttributes
    parsedMessage?.eventType || // Inside parsed Message
    data?.eventType || // Old structure
    data?.operation || // MC operation
    data?.Type || // SNS Type field
    "N/A"
  );
};

/**
 * Extract deviceId from multiple possible locations
 */
export const extractDeviceId = (data, parsedMessage) => {
  return (
    parsedMessage?.deviceId || // Inside parsed Message
    parsedMessage?.notifications?.[0]?.deviceId || // NOTIFICATION_REPORTED events
    data?.deviceId || // Old structure
    data?.innerMessage?.deviceId || // Old nested structure
    "N/A"
  );
};

/**
 * Extract actionId if present
 */
export const extractActionId = (data, parsedMessage) => {
  return (
    parsedMessage?.actionId || // Inside parsed Message
    parsedMessage?.notifications?.[0]?.actionId || // NOTIFICATION_REPORTED events
    data?.innerMessage?.actionId || // Old nested structure
    null
  );
};

/**
 * Extract installationId
 */
export const extractInstallationId = (data, parsedMessage) => {
  return (
    parsedMessage?.installationId || // Inside parsed Message
    parsedMessage?.notifications?.[0]?.installationId || // NOTIFICATION_REPORTED events
    data?.installationId || // Old structure
    null
  );
};

/**
 * Extract actionStatus for ACTION events
 */
export const extractActionStatus = (data, parsedMessage) => {
  return (
    parsedMessage?.actionStatus || // Inside parsed Message
    data?.innerMessage?.actionStatus || // Old structure
    null
  );
};

/**
 * Extract traitName for STATE_UPDATED and NOTIFICATION events
 */
export const extractTraitName = (data, parsedMessage) => {
  return (
    parsedMessage?.traitName || // Inside parsed Message
    parsedMessage?.notifications?.[0]?.traitName || // NOTIFICATION_REPORTED events
    data?.innerMessage?.traitName || // Old structure
    null
  );
};

/**
 * Extract webhook headers from metadata
 * YP events: headers are in _devStreamMetadata.webhookHeaders
 * MC events: headers are in data.headers
 */
export const extractWebhookHeaders = (data, source) => {
  return (
    data?._devStreamMetadata?.webhookHeaders ||
    (source === "MC" ? data?.headers : null)
  );
};

/**
 * Get raw event body (extract actual event content, not our wrapper)
 */
export const getRawEventBody = (data, source) => {
  if (!data) return null;

  // For MC events: extract just the payload
  if (source === "MC" && data.payload) {
    return data.payload;
  }

  // For YP events (SNS): remove our wrapper fields
  const { source: src, timestamp: ts, _devStreamMetadata, ...rawSNS } = data;

  // Return raw SNS message (UnsubscribeURL already redacted by backend)
  return rawSNS;
};

/**
 * Extract installation ID from TopicArn for SubscriptionConfirmation events
 */
export const extractSubscriptionInstallationId = (data, eventType) => {
  return eventType === "SubscriptionConfirmation"
    ? data?.TopicArn?.match(/webhooks_installation_id_(.+)$/)?.[1] ||
        data?.InstallationId
    : null;
};

/**
 * Check if event is orphaned (no organizationId)
 */
export const isOrphanedEvent = (data, source) => {
  const eventOrgId = data?._devStreamMetadata?.organizationId;
  return !eventOrgId && source !== "SYSTEM_ERROR";
};

/**
 * Format timestamp to HH:MM:SS.mmm format
 */
export const formatTime = (isoString) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch (e) {
    return isoString;
  }
};

/**
 * Get CSS class for source badge
 */
export const getSourceClass = (source) => {
  switch (source) {
    case "YP":
      return "source-yp";
    case "MC":
      return "source-mc";
    case "SYSTEM_ERROR":
      return "source-error";
    default:
      return "source-unknown";
  }
};
